import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { sbGetSetting, sbSetSetting } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import { parseMarkdown } from "../utils/markdownParser";
import AssessmentEditModal from "./AssessmentEditModal";
import PreviewModal from "./PreviewModal";
import PullToRefresh from "./PullToRefresh";
import { SVG_ICONS } from "./TablerIconPicker";
import "../styles/ListTab.css";

const AssessmentListTab = forwardRef(({ onHeaderInfoChange }, ref) => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  useEffect(() => {
    loadAssessments();
  }, []);

  useImperativeHandle(ref, () => ({
    loadAssessments
  }));

  useEffect(() => {
    onHeaderInfoChange?.({ title: "Assessments", count: assessments.length });
  }, [assessments, onHeaderInfoChange]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const data = await sbGetSetting("origamic-custom-assessments");
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
        : [];
      setAssessments(sorted);
    } catch (err) {
      console.error("Error loading assessments:", err);
      alert("Failed to load assessments");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this assessment?"))
      return;

    try {
      const updated = assessments.filter((a) => a.id !== id);
      await sbSetSetting("origamic-custom-assessments", updated);
      setAssessments(updated);
      alert("Assessment deleted successfully!");
    } catch (err) {
      console.error("Error deleting assessment:", err);
      alert("Failed to delete assessment");
    }
  };

  const handleEdit = (assessment) => {
    setEditingId(assessment.id);
  };

  const handleSaveEdit = async (updatedAssessment) => {
    try {
      console.log("📋 [CLIPBOARD] Saving assessment:", {
        id: updatedAssessment.id,
        name: updatedAssessment.name,
        questionsCount: updatedAssessment.questions?.length || 0,
        questions: updatedAssessment.questions,
        fullObject: updatedAssessment
      });

      const oldAssessment = assessments.find((a) => a.id === updatedAssessment.id);
      const wasPublished = !oldAssessment?.live && updatedAssessment.live;

      console.log("🔍 Publish check:", {
        oldLive: oldAssessment?.live,
        newLive: updatedAssessment.live,
        wasPublished
      });

      const updated = assessments.map((a) =>
        a.id === updatedAssessment.id ? updatedAssessment : a
      );
      await sbSetSetting("origamic-custom-assessments", updated);
      console.log("Assessment saved to Supabase");

      // Create notification if assessment was just published
      if (wasPublished) {
        console.log("📢 Assessment published - creating notification");
        const notifyResult = await sendNotification(
          `New Assessment: ${updatedAssessment.name}`,
          `${updatedAssessment.description || "Take this assessment to evaluate your AI readiness."}`,
          "assessment",
          updatedAssessment.id
        );
        console.log("📢 Notification result:", notifyResult);
      } else {
        console.log("⏭️ Skipped notification - assessment not being published");
      }

      setAssessments(updated);
      setEditingId(null);
      alert("Assessment updated successfully!");
    } catch (err) {
      console.error("Error saving assessment:", err);
      alert("Failed to save assessment");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleToggleDraft = async (assessment) => {
    try {
      const newLiveStatus = !assessment.live;
      const updated = assessments.map(a =>
        a.id === assessment.id ? { ...a, live: newLiveStatus } : a
      );
      await sbSetSetting("origamic-custom-assessments", updated);

      if (newLiveStatus === true) {
        // Publishing: send notification
        await sendNotification(
          `New Assessment: ${assessment.name}`,
          assessment.description || "Take this assessment to evaluate your AI readiness.",
          "assessment",
          assessment.id
        );
      }

      setAssessments(updated);
    } catch (err) {
      console.error("Error toggling live status:", err);
      alert("Failed to update live status");
    }
  };

  const handlePreview = (assessment) => {
    const questions = Array.isArray(assessment.questions) ? assessment.questions : [];
    let questionsContent = null;

    if (questions.length > 0) {
      questionsContent = (
        <div>
          {questions.map((q, idx) => {
            let question = '';
            let options = [];

            if (typeof q === 'string') {
              question = q;
            } else if (typeof q === 'object' && q !== null) {
              question = q.q || q.question || q.text || '';
              options = Array.isArray(q.opts) ? q.opts : Array.isArray(q.answers) ? q.answers : [];
            }

            return (
              <div key={idx} style={{ marginBottom: "24px", paddingBottom: "20px", borderBottom: "1px solid rgba(59, 130, 246, 0.2)" }}>
                <strong style={{ color: "#5B9EF0", display: "block", marginBottom: "12px", fontSize: "18px", fontWeight: "700" }}>
                  Q{idx + 1}. {question}
                </strong>
                <div style={{ marginLeft: "20px" }}>
                  {options.length > 0 ? (
                    options.map((opt, oidx) => {
                      const optionLabel = typeof opt === 'string' ? opt : opt.label || opt.text || '';
                      return (
                        <div key={oidx} style={{ marginBottom: "10px", color: "#7A9CC4", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                          <input type="radio" disabled style={{ cursor: "default", marginTop: "2px", flexShrink: 0 }} />
                          <span>{optionLabel}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ color: "#9DB4D4", fontStyle: "italic", fontSize: "13px" }}>No options provided</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    } else {
      questionsContent = (
        <div style={{ padding: "20px", background: "rgba(248, 113, 113, 0.1)", borderRadius: "6px", border: "1px solid rgba(248, 113, 113, 0.2)", color: "#f87171", textAlign: "center" }}>
          No questions found in this assessment
        </div>
      );
    }

    const content = (
      <div style={{ width: "100%", maxWidth: "100%", padding: "0 20px" }}>
        <h1 style={{ color: "#5B9EF0", marginTop: 0, fontSize: "28px" }}>{assessment.name || "Untitled Assessment"}</h1>
        <div style={{ color: "#7A9CC4", fontSize: "14px", marginBottom: "24px", padding: "8px 12px", background: "rgba(59, 130, 246, 0.05)", borderRadius: "4px", display: "inline-block" }}>
          {questions.length} {questions.length === 1 ? "question" : "questions"}
        </div>
        {assessment.description && (
          <div style={{ marginBottom: "32px", paddingTop: "16px", borderTop: "1px solid rgba(59, 130, 246, 0.15)" }}>
            <h3 style={{ color: "#5B9EF0", marginTop: 0 }}>Description</h3>
            <div dangerouslySetInnerHTML={{ __html: parseMarkdown(assessment.description) }} />
          </div>
        )}
        <div>
          <h3 style={{ color: "#5B9EF0", marginTop: "32px" }}>Questions</h3>
          {questionsContent}
        </div>
      </div>
    );

    setPreviewTitle(`Assessment: ${assessment.name}`);
    setPreviewContent(content);
    setPreviewOpen(true);
  };

  if (loading)
    return <div className="loading">Loading assessments...</div>;

  if (assessments.length === 0)
    return (
      <div className="empty-state">
        No assessments found. Create one first!
      </div>
    );

  const filteredAssessments = assessments.filter((assessment) =>
    assessment.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    assessment.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const editingAssessment = assessments.find((a) => a.id === editingId);

  return (
    <div className="list-container">
      <input
        type="text"
        placeholder="Search assessments..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          marginBottom: "20px",
          background: "#050e1a",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "6px",
          color: "#fff",
          fontSize: "14px",
          boxSizing: "border-box",
        }}
      />

      <PreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        content={previewContent}
        title={previewTitle}
      />

      {editingId && editingAssessment ? (
        <AssessmentEditModal
          assessment={editingAssessment}
          onSave={handleSaveEdit}
          onCancel={handleCancel}
        />
      ) : (
        <PullToRefresh onRefresh={loadAssessments}>
          {filteredAssessments.length === 0 && searchQuery && (
            <div className="empty-state">No assessments match your search</div>
          )}
          {filteredAssessments.length > 0 && (
          <div className="items-grid">
            {[...filteredAssessments]
              .sort(
                (a, b) =>
                  new Date(b.addedAt || 0) - new Date(a.addedAt || 0)
              )
              .map((assessment) => (
              <div key={assessment.id} className="item-card">
                <div className="item-icon">
                  {SVG_ICONS["clipboard-list"]({ size: 28, color: "#5B9EF0" })}
                </div>
                <div className="item-content">
                  <h4>{assessment.name}</h4>
                  <p className="item-meta">
                    {assessment.questions?.length || 0} questions •{" "}
                    {assessment.addedAt
                      ? new Date(assessment.addedAt).toLocaleDateString()
                      : ""}
                  </p>
                  {assessment.description && (
                    <p className="item-subtitle">
                      {assessment.description}
                    </p>
                  )}
                  <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                    {assessment.live ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 12px",
                          background: "#22C55E20",
                          color: "#22C55E",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600"
                        }}
                      >
                        {SVG_ICONS["check-circle"]({ size: 14, color: "#22C55E" })}
                        LIVE
                      </div>
                    ) : (
                      <div style={{display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "rgba(245, 158, 11, 0.2)", color: "#F59E0B", borderRadius: "6px", fontSize: "12px", fontWeight: "600"}}>
                        {SVG_ICONS["alert-circle"]({ size: 14, color: "#F59E0B" })}
                        DRAFT
                      </div>
                    )}
                  </div>
                </div>
                <div className="item-actions">
                  <button
                    onClick={() => handlePreview(assessment)}
                    title="Preview"
                    style={{
                      padding: "6px 12px",
                      background: "rgba(59, 130, 246, 0.1)",
                      color: "#5B9EF0",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.2)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.1)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                    }}
                  >
                    {SVG_ICONS["eye"]({ size: 18, color: "#5B9EF0" })}
                  </button>
                  <button
                    onClick={() => handleToggleDraft(assessment)}
                    title={assessment.live ? "Revert to draft" : "Publish assessment"}
                    style={{
                      padding: "6px 12px",
                      background: assessment.live ? "rgba(245, 158, 11, 0.1)" : "rgba(34, 197, 94, 0.1)",
                      color: assessment.live ? "#F59E0B" : "#22C55E",
                      border: assessment.live ? "1px solid rgba(245, 158, 11, 0.2)" : "1px solid rgba(34, 197, 94, 0.2)",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                    onMouseEnter={(e) => {
                      if (assessment.live) {
                        e.target.style.background = "rgba(245, 158, 11, 0.2)";
                        e.target.style.borderColor = "rgba(245, 158, 11, 0.4)";
                      } else {
                        e.target.style.background = "rgba(34, 197, 94, 0.2)";
                        e.target.style.borderColor = "rgba(34, 197, 94, 0.4)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (assessment.live) {
                        e.target.style.background = "rgba(245, 158, 11, 0.1)";
                        e.target.style.borderColor = "rgba(245, 158, 11, 0.2)";
                      } else {
                        e.target.style.background = "rgba(34, 197, 94, 0.1)";
                        e.target.style.borderColor = "rgba(34, 197, 94, 0.2)";
                      }
                    }}
                  >
                    {assessment.live
                      ? SVG_ICONS["circle-x"]({ size: 18, color: "#F59E0B" })
                      : SVG_ICONS["check"]({ size: 18, color: "#22C55E" })}
                    <span style={{ fontSize: "12px" }}>
                      {assessment.live ? "Draft" : "Publish"}
                    </span>
                  </button>
                  <button
                    onClick={() => handleEdit(assessment)}
                    style={{
                      padding: "6px 12px",
                      background: "rgba(59, 130, 246, 0.1)",
                      color: "#5B9EF0",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.2)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.1)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                    }}
                  >
                    {SVG_ICONS["pencil"]({ size: 18, color: "#5B9EF0" })}
                  </button>
                  <button
                    onClick={() => handleDelete(assessment.id)}
                    style={{
                      padding: "6px 12px",
                      background: "rgba(59, 130, 246, 0.1)",
                      color: "#5B9EF0",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.2)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.1)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                    }}
                  >
                    {SVG_ICONS["trash"]({ size: 18, color: "#5B9EF0" })}
                  </button>
                </div>
              </div>
              ))}
          </div>
          )}
        </PullToRefresh>
      )}
    </div>
  );
});

AssessmentListTab.displayName = 'AssessmentListTab';

export default AssessmentListTab;
